/**
 * Application policies layered over the Rust-owned, OpenAPI-generated HTTP client.
 *
 * Generated code owns wire types, paths, request construction, and response parsing.
 * This module owns behavior that generation cannot decide, such as contextual errors,
 * data normalization, cache invalidation, and stable application-facing constants.
 * It also temporarily re-exports generated types under existing import names while
 * callers migrate; new code should import generated types and simple operations directly.
 */
// TODO(1): Remove compatibility-only aliases and forwarding wrappers as their callers move to
// generated contracts. Move the behavior-rich policies that remain into focused domain modules
// (such as auth, documents, and profiles) so this catch-all compatibility facade can disappear.
import { useEffect, useState } from 'react';

import useSWR, { mutate } from 'swr';

import {
  addOfferToCollectionByUrl as generatedAddOfferToCollectionByUrl,
  AvatarSourceDto as GeneratedAvatarSource,
  AvatarValidationErrorCode as GeneratedAvatarValidationErrorCode,
  castVote as generatedCastVote,
  checkPasswordStrength as generatedCheckPasswordStrength,
  checkUsernameAvailability as generatedCheckUsernameAvailability,
  commitAdminSchemaJudgments as generatedCommitAdminSchemaJudgments,
  createDoc as generatedCreateDoc,
  deleteAccount as generatedDeleteAccount,
  DocVisibility as GeneratedDocVisibility,
  EmailUnsupportedReasonDto as GeneratedEmailUnsupportedReason,
  getAvatarSettings as generatedGetAvatarSettings,
  getAdminSchemaView as generatedGetAdminSchemaView,
  getGetAdminSchemaViewUrl,
  getGetSchemaByRefUrl,
  getSearchAdminConceptsByPrefixUrl,
  getSearchConceptsByPrefixUrl,
  getSchemaByRef as generatedGetSchemaByRef,
  HealthEvent as GeneratedHealthEvent,
  HealthStatus as GeneratedHealthStatus,
  hideDoc as generatedHideDoc,
  OfferPostType as GeneratedOfferPostType,
  Marketplace as GeneratedMarketplace,
  PasswordStateDto as GeneratedPasswordState,
  ProfileDiscoverability as GeneratedProfileDiscoverability,
  RequestError as GeneratedRequestError,
  RequestPasswordResetStatus as GeneratedRequestPasswordResetStatus,
  RequestRejection as GeneratedRequestRejection,
  ResendEmailVerificationStatus as GeneratedResendEmailVerificationStatus,
  ResetPasswordStatus as GeneratedResetPasswordStatus,
  patchAvatarSettings as generatedPatchAvatarSettings,
  previewAvatar as generatedPreviewAvatar,
  register as generatedRegister,
  resendEmailVerification as generatedResendEmailVerification,
  stopEmailVerification as generatedStopEmailVerification,
  StopEmailVerificationStatus as GeneratedStopEmailVerificationStatus,
  SuccessStatus as GeneratedSuccessStatus,
  updateDiscoverability as generatedUpdateDiscoverability,
  updateCollections as generatedUpdateCollections,
  updateEmail as generatedUpdateEmail,
  updateFollows as generatedUpdateFollows,
  UpdateCollections as GeneratedUpdateCollections,
  UpdateEmailStatus as GeneratedUpdateEmailStatus,
  UpdateFollowAction as GeneratedUpdateFollowAction,
  updateMarketplacePreference as generatedUpdateMarketplacePreference,
  validateResetToken as generatedValidateResetToken,
  ValidateResetTokenStatus as GeneratedValidateResetTokenStatus,
  verifyEmail as generatedVerifyEmail,
  VerifyEmailStatus as GeneratedVerifyEmailStatus,
} from './api/generated';
import type {
  AdminSchemaCommitJudgmentsRequest as GeneratedAdminSchemaCommitJudgmentsRequest,
  AdminSchemaCommitJudgmentsResponse as GeneratedAdminSchemaCommitJudgmentsResponse,
  AdminSchemaViewResponse as GeneratedAdminSchemaViewResponse,
  AvatarPreviewRequest as GeneratedAvatarPreviewRequest,
  AvatarSettingsResponse as GeneratedAvatarSettingsResponse,
  AvatarValidationErrorsDto as GeneratedAvatarValidationErrors,
  AvatarValidationErrorResponse as GeneratedAvatarValidationErrorResponse,
  CastVoteRequest,
  CastVoteResponse,
  DefaultPlaceholderAvatarConfigDto as GeneratedDefaultPlaceholderAvatarConfig,
  DisplayCollection as GeneratedDisplayCollection,
  DisplayConceptRef as GeneratedDisplayConceptRef,
  DisplayUser as GeneratedDisplayUser,
  DisplayUserFollow as GeneratedDisplayUserFollow,
  DisclosedVoteInfo,
  GeneratedInitialsAvatarConfigDto as GeneratedInitialsAvatarConfigContract,
  NewDocRequest as GeneratedNewDocRequest,
  RegistrationResponse as GeneratedRegistrationResponse,
  UpdateEmailResponse as GeneratedUpdateEmailResponse,
  VoteAction,
  VoteState,
} from './api/generated';
import type * as ApiContract from './api/generated';
import { API_URL } from './consts';
import { DISPLAY_IMAGE_CDN, type DisplayImage } from './displayImageContract';
import type { OAuthIdentityInfo, OAuthProvider } from './oauth';

export { buildDocThreadView } from './docThreadView';
export type { DocThreadView } from './docThreadView';
export { BgTaskStatus } from './api/generated-websocket/BgTaskStatus';
export type { CastVoteResponse, DisclosedVoteInfo, VoteAction, VoteState } from './api/generated';

export type PublicDocId = ApiContract.PublicDocId;

export function asPublicDocId(value: string): PublicDocId {
  return value as PublicDocId;
}

/** Brands a stable account identifier using the Rust-generated API boundary type. */
export function asPublicUserId(value: string): ApiContract.PublicUserId {
  return value as ApiContract.PublicUserId;
}

/**
 * Stable facade names for generated client error discriminants.
 *
 * The `satisfies` constraint catches values not present in the Rust-owned contract.
 */
export const CLIENT_ERROR_CODE = {
  NoAuth: 'no_auth',
  ServiceError: 'service_error',
  RegFail: 'reg_fail',
  RequestRejected: 'request_rejected',
  InvalidRequestData: 'invalid_request_data',
  OAuthCodeAlreadyUsed: 'oauth_code_already_used',
  Forbidden: 'forbidden',
  UserNotFound: 'user_not_found',
  ProductNotFound: 'product_not_found',
  InvalidProductReference: 'invalid_product_reference',
  RegistrationLoginFailed: 'registration_login_failed',
  DocumentNotFound: 'document_not_found',
} as const satisfies Record<string, ApiContract.ClientError['type']>;
/** Union of client error identifiers returned by server error middleware. */
export type ClientErrorCode = (typeof CLIENT_ERROR_CODE)[keyof typeof CLIENT_ERROR_CODE];

/** Stable facade names for generated safe HTTP-rejection reasons. */
export const REQUEST_REJECTION = {
  MalformedRequest: GeneratedRequestRejection.malformed_request,
  RouteNotFound: GeneratedRequestRejection.route_not_found,
  MethodNotAllowed: GeneratedRequestRejection.method_not_allowed,
  PayloadTooLarge: GeneratedRequestRejection.payload_too_large,
  UriTooLong: GeneratedRequestRejection.uri_too_long,
  UnsupportedMediaType: GeneratedRequestRejection.unsupported_media_type,
  UnprocessableEntity: GeneratedRequestRejection.unprocessable_entity,
  TooManyRequests: GeneratedRequestRejection.too_many_requests,
  Other: GeneratedRequestRejection.other,
} as const;
type RequestRejection = ApiContract.RequestRejection;

/** Stable facade names for generated request-validation reasons. */
export const REQUEST_ERROR = {
  OnlyAuthenticationMethod: GeneratedRequestError.only_authentication_method,
  OAuthIdentityNotFound: GeneratedRequestError.oauth_identity_not_found,
  InvalidUrl: GeneratedRequestError.invalid_url,
  UnsupportedMarketplace: GeneratedRequestError.unsupported_marketplace,
  ProductIdMissing: GeneratedRequestError.product_id_missing,
  SearchQueryTooLong: GeneratedRequestError.search_query_too_long,
  InvalidInviteCreditQuantity: GeneratedRequestError.invalid_invite_credit_quantity,
  InvalidConceptReference: GeneratedRequestError.invalid_concept_reference,
  JustificationTooLong: GeneratedRequestError.justification_too_long,
  InvalidJudgmentSubject: GeneratedRequestError.invalid_judgment_subject,
  InvalidJudgmentRelation: GeneratedRequestError.invalid_judgment_relation,
  InvalidJudgmentObject: GeneratedRequestError.invalid_judgment_object,
  ReflexiveAssertion: GeneratedRequestError.reflexive_assertion,
  UnsupportedRelation: GeneratedRequestError.unsupported_relation,
  InvalidQuality: GeneratedRequestError.invalid_quality,
  EmptyJudgments: GeneratedRequestError.empty_judgments,
  TooManyJudgments: GeneratedRequestError.too_many_judgments,
  DuplicateJudgment: GeneratedRequestError.duplicate_judgment,
  InvalidJudgmentRequest: GeneratedRequestError.invalid_judgment_request,
} as const;
type RequestError = ApiContract.RequestError;

/** UI text for actionable validation failures previously described by backend strings. */
const REQUEST_ERROR_MESSAGES: Record<RequestError, string> = {
  [REQUEST_ERROR.OnlyAuthenticationMethod]:
    'Cannot remove your only authentication method. Please set a password first.',
  [REQUEST_ERROR.OAuthIdentityNotFound]: 'OAuth identity not found.',
  [REQUEST_ERROR.InvalidUrl]: 'Invalid URL format.',
  [REQUEST_ERROR.UnsupportedMarketplace]: 'This marketplace is not supported.',
  [REQUEST_ERROR.ProductIdMissing]: 'Could not extract a product ID from the URL.',
  [REQUEST_ERROR.SearchQueryTooLong]: 'Search query is too long.',
  [REQUEST_ERROR.InvalidInviteCreditQuantity]: 'Invitation credit quantity must be positive.',
  [REQUEST_ERROR.InvalidConceptReference]: 'Invalid concept reference.',
  [REQUEST_ERROR.JustificationTooLong]: 'Judgment justification is too long.',
  [REQUEST_ERROR.InvalidJudgmentSubject]: 'Judgment subject is not a valid concept reference.',
  [REQUEST_ERROR.InvalidJudgmentRelation]: 'Judgment relation is not a valid concept reference.',
  [REQUEST_ERROR.InvalidJudgmentObject]: 'Judgment object is not a valid concept reference.',
  [REQUEST_ERROR.ReflexiveAssertion]: 'Reflexive assertions are not allowed.',
  [REQUEST_ERROR.UnsupportedRelation]: 'Judgment relation is not supported.',
  [REQUEST_ERROR.InvalidQuality]: 'Concept quality is not a canonical quality value.',
  [REQUEST_ERROR.EmptyJudgments]: 'At least one judgment is required.',
  [REQUEST_ERROR.TooManyJudgments]: 'Too many judgments in one commit.',
  [REQUEST_ERROR.DuplicateJudgment]: 'Duplicate literal judgment triple.',
  [REQUEST_ERROR.InvalidJudgmentRequest]: 'Invalid admin schema judgment request.',
};

/** Generated shared HTTP error envelope. */
type ApiErrorEnvelope = ApiContract.ApiErrorEnvelope;

const CLIENT_ERROR_CODES = new Set<ClientErrorCode>(Object.values(CLIENT_ERROR_CODE));
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Safely narrows an untrusted server response to the shared code-only error contract. */
export function parseApiErrorEnvelope(value: unknown): ApiErrorEnvelope | null {
  if (typeof value !== 'object' || value === null || !('error' in value)) return null;
  if (Object.keys(value).length !== 1) return null;

  const error = value.error;
  if (typeof error !== 'object' || error === null) return null;

  const candidate = error as Record<string, unknown>;
  if (
    typeof candidate.type !== 'string' ||
    !CLIENT_ERROR_CODES.has(candidate.type as ClientErrorCode) ||
    typeof candidate.req_uuid !== 'string' ||
    !UUID_PATTERN.test(candidate.req_uuid)
  ) {
    return null;
  }

  if (candidate.type === CLIENT_ERROR_CODE.InvalidRequestData) {
    if (
      Object.keys(candidate).length !== 3 ||
      !Object.values(REQUEST_ERROR).includes(candidate.reason as RequestError)
    )
      return null;
    return {
      error: {
        type: candidate.type,
        reason: candidate.reason as RequestError,
        req_uuid: candidate.req_uuid,
      },
    };
  }
  if (candidate.type === CLIENT_ERROR_CODE.RequestRejected) {
    if (
      Object.keys(candidate).length !== 3 ||
      !Object.values(REQUEST_REJECTION).includes(candidate.reason as RequestRejection)
    )
      return null;
    return {
      error: {
        type: candidate.type,
        reason: candidate.reason as RequestRejection,
        req_uuid: candidate.req_uuid,
      },
    };
  }
  if (Object.keys(candidate).length !== 2) return null;

  return {
    error: {
      type: candidate.type as Exclude<
        ClientErrorCode,
        typeof CLIENT_ERROR_CODE.InvalidRequestData | typeof CLIENT_ERROR_CODE.RequestRejected
      >,
      req_uuid: candidate.req_uuid,
    },
  };
}

/** Reads the shared error envelope without exposing arbitrary response content. */
export async function parseApiErrorResponse(response: Response): Promise<ApiErrorEnvelope | null> {
  try {
    return parseApiErrorEnvelope(await response.json());
  } catch {
    return null;
  }
}

/** @public Stable application name for the generated success acknowledgement. */
export const SUCCESS_STATUS = {
  Success: GeneratedSuccessStatus.success,
} as const;
/** @public Successful acknowledgement status shared by simple API responses. */
export type SuccessStatus = ApiContract.SuccessStatus;

/** @public Generated acknowledgement returned by simple successful mutations. */
export type SuccessResponse = ApiContract.SuccessResponse;

/** Generated session-validity response used by login and logout polling. */
export type AuthCheckResponse = ApiContract.AuthCheckResponse;

type ApiErrorContext =
  | { operation: 'login' }
  | { operation: 'password_change' }
  | { operation: 'oauth_removal'; provider: OAuthProvider }
  | { operation: 'registration' }
  | { operation: 'username_check' };

type StaticApiErrorOperation = Exclude<ApiErrorContext['operation'], 'oauth_removal'>;

const API_ERROR_MESSAGES: Record<StaticApiErrorOperation, string> = {
  login: 'Login failed. Please check your credentials and try again.',
  password_change: 'Failed to change password.',
  registration: 'Registration failed. Please try again.',
  username_check: 'Failed to check username availability.',
};

/** Maps status and machine code to context-owned user-facing text. */
export function apiErrorMessage(
  context: ApiErrorContext,
  status: number,
  envelope: ApiErrorEnvelope | null
): string {
  if (envelope?.error.type === CLIENT_ERROR_CODE.InvalidRequestData) {
    return REQUEST_ERROR_MESSAGES[envelope.error.reason];
  }
  if (envelope?.error.type === CLIENT_ERROR_CODE.RegistrationLoginFailed) {
    return 'Your account was created, but automatic sign-in failed. Please sign in.';
  }
  if (context.operation === 'login' && (status === 401 || status === 403)) {
    return API_ERROR_MESSAGES.login;
  }
  if (context.operation === 'password_change' && status === 403) {
    return 'Your current password could not be verified.';
  }
  if (envelope?.error.type === CLIENT_ERROR_CODE.NoAuth) {
    return 'Your session has expired. Please log in and try again.';
  }
  if (context.operation === 'oauth_removal') {
    return `Failed to remove the ${context.provider} connection.`;
  }
  return API_ERROR_MESSAGES[context.operation];
}

/**
 * Facade names for generated document visibility identifiers.
 */
export const DOC_VISIBILITY = {
  Public: GeneratedDocVisibility.public,
  Friends: GeneratedDocVisibility.friends,
  Private: GeneratedDocVisibility.private,
} as const;
export type DocVisibility = ApiContract.DocVisibility;

/** Enumerates document visibility values in menu and validation order. */
export const DOC_VISIBILITY_VALUES = [
  DOC_VISIBILITY.Public,
  DOC_VISIBILITY.Friends,
  DOC_VISIBILITY.Private,
] as const satisfies readonly DocVisibility[];

/** Assigns a numeric restrictiveness rank to each document visibility. */
export const DOC_VISIBILITY_ORDER: Record<DocVisibility, number> = {
  [DOC_VISIBILITY.Public]: 0,
  [DOC_VISIBILITY.Friends]: 1,
  [DOC_VISIBILITY.Private]: 2,
};

/**
 * Facade names for generated follow action identifiers.
 */
export const UPDATE_FOLLOW_ACTION = {
  Follow: GeneratedUpdateFollowAction.follow,
  Unfollow: GeneratedUpdateFollowAction.unfollow,
  Update: GeneratedUpdateFollowAction.update,
} as const;
export type UpdateFollowAction = ApiContract.UpdateFollowAction;

/**
 * Facade names for generated collection action identifiers.
 */
export const UPDATE_COLLECTIONS_ACTION = {
  Add: GeneratedUpdateCollections.add,
  Remove: GeneratedUpdateCollections.remove,
} as const;
export type UpdateCollectionsAction = ApiContract.UpdateCollections;

export { DISPLAY_IMAGE_CDN, type DisplayImage };

// A concept reference, which is a unique identifier for a concept.  Used
// for both general concepts (topics) and specific offers.
export type DisplayConceptRef = GeneratedDisplayConceptRef;

/** Namespace prefix identifying offer concept references. */
export const OFFER_NAMESPACE = '/sku/';

// TODO(1): use consistently; there are places in the code base where ad-hoc
// structures are used instead of this.
export type DisplayConceptDef = ApiContract.SchemaConceptResponse;

/** @public Names shared by the backend's internal recon classification and response buckets. */
export const RECON_RELATION = {
  Disjoint: 'disjoint',
  Different: 'different',
  Overlap: 'overlap',
  MapsTo: 'maps_to',
  MappedFrom: 'mapped_from',
  Superclass: 'superclass',
  Subclass: 'subclass',
  Other: 'other',
} as const satisfies Record<string, keyof ApiContract.ReconInfoResponse>;
/** @public Relation bucket names returned by recon endpoints. */
export type ReconRelation = (typeof RECON_RELATION)[keyof typeof RECON_RELATION];

// TODO(1): Publish the admin judgment vocabulary through a Rust-owned contract instead of
// mirroring ontology constants here. `TripleDto` intentionally accepts general concept refs, so
// its schema cannot currently express the narrower set offered by this UI.
/**
 * Structural relation wire identifiers accepted by the admin schema endpoint.
 * The backend validates these concept references against its supported-relation allowlist.
 */
export const STRUCTURAL_RELATION = {
  Same: '/core/same',
  SubclassOf: '/core/subclassof',
  SuperclassOf: '/core/superclassof',
  Overlap: '/core/overlap',
  Disjoint: '/core/disjoint',
  Different: '/core/different',
} as const;

/** Identifies one structural relation accepted by the admin schema endpoint. */
export type StructuralRelation = (typeof STRUCTURAL_RELATION)[keyof typeof STRUCTURAL_RELATION];

/** Generated triple projection used by the admin schema view. */
export type TripleDto = ApiContract.TripleDto;

/** Canonical relation used by concept-quality judgments. */
export const CONCEPT_QUALITY_RELATION = '/concept/quality';

/**
 * Canonical concept-quality judgment values.
 * The backend accepts these objects when the judgment relation is `CONCEPT_QUALITY_RELATION`.
 */
export const CONCEPT_QUALITY = {
  Good: '/concept/quality/good',
  Compositional: '/concept/quality/compositional',
  Broad: '/concept/quality/broad',
  Specific: '/concept/quality/specific',
  Ambiguous: '/concept/quality/ambiguous',
  Nonproduct: '/concept/quality/nonproduct',
  Redundant: '/concept/quality/redundant',
  Nonexistent: '/concept/quality/nonexistent',
  Incoherent: '/concept/quality/incoherent',
} as const;

/** Identifies one canonical concept-quality judgment value. */
export type ConceptQualityValue = (typeof CONCEPT_QUALITY)[keyof typeof CONCEPT_QUALITY];

/** Enumerates canonical concept-quality values in classification order. */
export const CONCEPT_QUALITY_VALUES = [
  CONCEPT_QUALITY.Good,
  CONCEPT_QUALITY.Compositional,
  CONCEPT_QUALITY.Broad,
  CONCEPT_QUALITY.Specific,
  CONCEPT_QUALITY.Ambiguous,
  CONCEPT_QUALITY.Nonproduct,
  CONCEPT_QUALITY.Redundant,
  CONCEPT_QUALITY.Nonexistent,
  CONCEPT_QUALITY.Incoherent,
] as const satisfies readonly ConceptQualityValue[];

/** Generated judgment submitted through the admin schema endpoint. */
export type AdminSchemaJudgmentInputDto = ApiContract.AdminSchemaJudgmentInputDto;

/** Generated batch request for admin schema judgments. */
export type AdminSchemaCommitJudgmentsRequest = GeneratedAdminSchemaCommitJudgmentsRequest;

/** Generated result from committing admin schema judgments. */
export type AdminSchemaCommitJudgmentsResponse = GeneratedAdminSchemaCommitJudgmentsResponse;

export type EvidenceRowDto = ApiContract.EvidenceRowDto;

export type ExclusionDto = ApiContract.ExclusionDto;

export type ReducedTripleDto = ApiContract.ReducedTripleDto;

export type ComposedReducedTripleDto = ApiContract.ComposedReducedTripleDto;

export type LiteralSectionDto = ApiContract.LiteralSectionDto;

export type ComposedSectionDto = ApiContract.ComposedSectionDto;

export type AdminSchemaViewResponse = GeneratedAdminSchemaViewResponse;

// Utility function to convert DisplayConceptDef to DisplayConceptRef
export function convertConceptDefToRef(cdef: DisplayConceptDef): DisplayConceptRef {
  return {
    cref: cdef.key,
    display: cdef.display_name,
    description: cdef.description,
    is_offer: cdef.key.startsWith(OFFER_NAMESPACE), // TODO(1): This should come from backend once is_offer field is available
  };
}

/** Generated post-search excerpt. */
export type DocSearchResult = ApiContract.DocSearchResult;

/** Generated grouped conversation-search result. */
export type ThreadSearchResult = ApiContract.ThreadSearchResult;

/** Generated structured-document content. */
export type StructuredDoc = ApiContract.StructuredDocContent;

/**
 * Field-restricted projection of the same document represented by DisplayDoc,
 * not a distinct domain object. The separate type constrains the anonymous
 * response so new DisplayDoc fields cannot enter it implicitly.
 */
export type PublicReviewProjection = ApiContract.PublicReviewProjection;

// Block-level elements in the document
export type ContentBlock = ApiContract.ContentBlock;

// List item (can contain nested blocks)
export type StructuredListItem = ApiContract.ListItem;

// Table types
export type TableRow = ApiContract.TableRow;

export type TableCell = ApiContract.TableCell;

// Inline elements within a block
export type InlineElement = ApiContract.InlineElement;

// Offer comparison row
export type OfferComparisonRow = ApiContract.OfferComparisonRow;

// A document or post, as displayed to the user.
// All fields are actively used in the frontend based on audit performed
export type DisplayDoc = ApiContract.DisplayDoc;

export type DisplayCitation = ApiContract.DisplayCitation;

/** Runtime discriminants from the generated tagged thread-node contract. */
export const THREAD_NODE_KIND = {
  Document: 'document',
  Inaccessible: 'inaccessible',
} as const;

/** Generated tagged thread-node union. */
export type ThreadNode = ApiContract.ThreadNode;

/** Checks the required top-level shape consumed by feed document rendering. */
function isRuntimeDisplayDoc(value: unknown): value is DisplayDoc {
  if (typeof value !== 'object' || value === null) return false;

  /** Provides untyped access to the candidate document payload. */
  const candidate = value as Record<string, unknown>;
  /** Provides the nested provenance payload checked below. */
  const provenance = candidate.provenance;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.text === 'string' &&
    (candidate.topic === undefined ||
      candidate.topic === null ||
      (typeof candidate.topic === 'object' && candidate.topic !== null)) &&
    (candidate.aspect === undefined ||
      candidate.aspect === null ||
      (typeof candidate.aspect === 'object' && candidate.aspect !== null)) &&
    Array.isArray(candidate.citations) &&
    typeof provenance === 'object' &&
    provenance !== null &&
    typeof (provenance as Record<string, unknown>).creator === 'string' &&
    typeof (provenance as Record<string, unknown>).createdon === 'string' &&
    (candidate.visibility === null ||
      candidate.visibility === DOC_VISIBILITY.Public ||
      candidate.visibility === DOC_VISIBILITY.Friends ||
      candidate.visibility === DOC_VISIBILITY.Private) &&
    Array.isArray(candidate.mentioned_concepts) &&
    Array.isArray(candidate.suggested_questions)
  );
}

/** Keeps only recognized runtime thread nodes from an unvalidated API payload. */
export function normalizeThreadNodes(nodes: unknown): ThreadNode[] {
  if (!Array.isArray(nodes)) return [];

  return nodes.filter((node): node is ThreadNode => {
    if (typeof node !== 'object' || node === null) return false;

    const candidate = node as Record<string, unknown>;
    if (typeof candidate.public_doc_id !== 'string') return false;
    if (
      candidate.parent_public_doc_id !== null &&
      typeof candidate.parent_public_doc_id !== 'string'
    ) {
      return false;
    }

    if (candidate.kind === THREAD_NODE_KIND.Inaccessible) return true;
    return (
      candidate.kind === THREAD_NODE_KIND.Document &&
      isRuntimeDisplayDoc(candidate.doc) &&
      candidate.doc.id === candidate.public_doc_id
    );
  });
}

/** Generated thread response returned by document and feed endpoints. */
export type ThreadResponse = ApiContract.ThreadResponse;

/** Generated suggested-question projection embedded in feed items. */
export type FeedItemSuggestedQuestion = ApiContract.FeedItemSuggestedQuestion;

// A single topic entry in a thread's table of contents
export type ThreadTopicEntry = ApiContract.ThreadTopicEntry;

// Table of contents for a thread
export type ThreadTableOfContents = ApiContract.ThreadTableOfContents;

/** Generated marketplace-offer projection returned by the backend. */
export type DisplayOffer = ApiContract.DisplayOffer;

// A feed item
export type FeedItem = ApiContract.FeedItem;

// Justification for a feed item
type FeedItemJustification = ApiContract.FeedItemJustification;

// Info about a follow for a user (typically the current user).  Contains
// the username of the followed user, and the topics for the follow.
export type DisplayUserFollow = GeneratedDisplayUserFollow;

// Some additional info for the user that's generally useful in the UI.
type DisplayCollection = GeneratedDisplayCollection;

// A consolidated grouping of the thick data for all the items in the
// user's collections.
export type CollectionsPayload = ApiContract.CollectionsPayload;

type AddOfferByUrlRequest = ApiContract.AddOfferByUrlRequest;

/**
 * Facade names for generated avatar source identifiers.
 */
export const AVATAR_SOURCE = {
  DefaultPlaceholder: GeneratedAvatarSource.default_placeholder,
  GeneratedInitials: GeneratedAvatarSource.generated_initials,
} as const;
export type AvatarSource = ApiContract.AvatarSourceDto;

export const AVATAR_SOURCE_DISPLAY_ORDER: readonly AvatarSource[] = [
  AVATAR_SOURCE.DefaultPlaceholder,
  AVATAR_SOURCE.GeneratedInitials,
] as const;

export function assertAvatarSourceDisplayOrderIsExhaustive(): void {
  const all = Object.values(AVATAR_SOURCE);
  const order = [...AVATAR_SOURCE_DISPLAY_ORDER];

  const unique = new Set(order);
  if (unique.size !== order.length) {
    throw new Error('Duplicate avatar source in display order');
  }

  const missing = all.filter((source) => !unique.has(source));
  const extra = order.filter((source) => !all.includes(source));

  if (missing.length > 0 || extra.length > 0) {
    throw new Error(
      `Avatar source display order mismatch: missing=${missing.join(',')} extra=${extra.join(',')}`
    );
  }
}

assertAvatarSourceDisplayOrderIsExhaustive();

export type DefaultPlaceholderAvatarConfig = GeneratedDefaultPlaceholderAvatarConfig;

export type GeneratedInitialsAvatarConfig = GeneratedInitialsAvatarConfigContract;

export type AvatarSettingsResponse = GeneratedAvatarSettingsResponse;

export type PatchAvatarSettingsRequest = ApiContract.PatchAvatarSettingsRequest;

export const AVATAR_VALIDATION_ERROR_CODE = {
  Required: GeneratedAvatarValidationErrorCode.required,
  TooLong: GeneratedAvatarValidationErrorCode.too_long,
  InvalidCharacters: GeneratedAvatarValidationErrorCode.invalid_characters,
  InvalidHexColor: GeneratedAvatarValidationErrorCode.invalid_hex_color,
} as const;
export type AvatarValidationErrorCode = ApiContract.AvatarValidationErrorCode;

export type AvatarValidationErrors = GeneratedAvatarValidationErrors;

/** Generated avatar-validation error payload. */
export type AvatarValidationErrorResponse = GeneratedAvatarValidationErrorResponse;

/** Generated avatar-preview request payload. */
export type AvatarPreviewRequest = GeneratedAvatarPreviewRequest;

type AvatarPreviewResult =
  | {
      success: true;
      svg: Blob;
    }
  | AvatarValidationErrorResponse;

export function resolveEffectiveAvatarUrl(url: string): string {
  if (url.startsWith('/user/avatar/')) {
    return `${API_URL}${url}`;
  }

  return url;
}

/**
 * Facade names for generated API-safe password-state identifiers.
 */
export const PASSWORD_STATE = {
  UserSet: GeneratedPasswordState.user_set,
  SystemGenerated: GeneratedPasswordState.system_generated,
  Temporary: GeneratedPasswordState.temporary,
  ResetRequired: GeneratedPasswordState.reset_required,
  ResetInitiated: GeneratedPasswordState.reset_initiated,
} as const;

export type DisplayUser = GeneratedDisplayUser;

/**
 * Facade names for generated profile-discoverability identifiers.
 */
export const PROFILE_DISCOVERABILITY = {
  Public: GeneratedProfileDiscoverability.public,
  Connections: GeneratedProfileDiscoverability.connections,
  Private: GeneratedProfileDiscoverability.private,
} as const;
export type ProfileDiscoverability = ApiContract.ProfileDiscoverability;

/** Enumerates profile discoverability values in UI display order. */
export const PROFILE_DISCOVERABILITY_VALUES = [
  PROFILE_DISCOVERABILITY.Public,
  PROFILE_DISCOVERABILITY.Connections,
  PROFILE_DISCOVERABILITY.Private,
] as const satisfies readonly ProfileDiscoverability[];

/**
 * Facade names for generated marketplace identifiers.
 */
export const MARKETPLACE = {
  Amazon: GeneratedMarketplace.amazon,
  Walmart: GeneratedMarketplace.walmart,
  Shopify: GeneratedMarketplace.shopify,
} as const;
export type Marketplace = ApiContract.Marketplace;

/** Maps marketplace identifiers to the labels shown in the frontend. */
export const MARKETPLACE_DISPLAY_NAMES: Record<Marketplace, string> = {
  [MARKETPLACE.Amazon]: 'Amazon',
  [MARKETPLACE.Walmart]: 'Walmart',
  [MARKETPLACE.Shopify]: 'Shopify',
};

/** Lists all supported marketplaces in the order expected by marketplace controls. */
export const ALL_MARKETPLACES = [
  MARKETPLACE.Amazon,
  MARKETPLACE.Walmart,
  MARKETPLACE.Shopify,
] as const satisfies readonly Marketplace[];

/** Generated shopping marketplace preference. */
export type MarketplacePreference = ApiContract.MarketplaceAffinity;

/** Generated minimal user-search result. */
export type UserSearchResult = ApiContract.UserSearchResult;

/** Generated public profile projection. */
export type PublicUserProfile = ApiContract.PublicUserProfile;

// A unified request to update follows (both topics and users)
type UpdateFollowsRequest = ApiContract.UpdateFollowsRequest;

//////////////////////////////////////////////////////////////////////
//
// Shared components that call the backend
//

// Not sure how widely these are used.  Also not sure if they should be
// used vs. the useSWR hook.
export enum LoadStatus {
  NoArg,
  Error,
  IsLoading,
  EmptyResponse,
  Success,
}

/**
 * Generic hook for fetching data with LoadStatus handling.
 *
 * @param url - The URL to fetch data from
 * @param argCheck - Whether the arguments are valid for fetching
 * @param fetcher - Generated operation that owns request construction and response parsing.
 * @param dataTransform - Optional transform function applied to fetched data.
 *   IMPORTANT: This function should be pure and not capture changing values.
 *   If your transform depends on reactive state, wrap it with useCallback:
 *   ```
 *   const transform = useCallback((data) =>
 *     data.filter(x => x.id === selectedId), [selectedId]
 *   );
 *   ```
 * @returns A tuple of [LoadStatus, transformed data or null]
 */
export function useGetData<T>(
  url: string,
  argCheck: boolean,
  fetcher: () => Promise<T>,
  dataTransform?: (data: T) => T
): [LoadStatus, T | null] {
  const { data, error, isLoading } = useSWR(url, fetcher);

  const [status, setStatus] = useState<LoadStatus>(LoadStatus.IsLoading);
  const [result, setResult] = useState<T | null>(null);

  useEffect(() => {
    if (!argCheck) {
      setStatus(LoadStatus.NoArg);
      setResult(null);
    } else if (error) {
      setStatus(LoadStatus.Error);
      setResult(null);
    } else if (isLoading) {
      setStatus(LoadStatus.IsLoading);
      setResult(null);
    } else if (!data || (typeof data === 'string' && data.length === 0)) {
      setStatus(LoadStatus.EmptyResponse);
      setResult(null);
    } else {
      setStatus(LoadStatus.Success);
      // Apply transformation if provided, otherwise use data as-is
      const transformedData = dataTransform ? dataTransform(data) : (data as T);
      setResult(transformedData);
    }
    // dataTransform intentionally excluded from deps - it's logically stable and callers
    // pass simple transform functions that don't capture changing values. Including it
    // causes infinite re-renders when callers use inline arrow functions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argCheck, data, error, isLoading]);

  return [status, result];
}

// Load a concept definition from the backend.
export function useGetCDef(topic: string): [LoadStatus, DisplayConceptDef | null] {
  // Encode cref keys as a single path segment so slash-containing RefH values
  // still match the backend `/schema/{refs}` route. Callers must pass decoded
  // cref strings into this hook.
  const url = topic ? `${API_URL}${getGetSchemaByRefUrl(topic)}` : '';
  return useGetData<DisplayConceptDef>(url, topic !== '', () => generatedGetSchemaByRef(topic));
}

/** Builds the SWR key for one unified admin schema view. */
export function adminSchemaViewUrl(cref: string): string {
  return cref ? `${API_URL}${getGetAdminSchemaViewUrl(cref)}` : '';
}

/** Public concept-search endpoint consumed by navigation-oriented selectors. */
export const SCHEMA_PREFIX_SEARCH_PATH = getSearchConceptsByPrefixUrl('').replace(/\/$/, '');

/** Admin concept-search endpoint that includes schema-editor concepts. */
export const ADMIN_SCHEMA_PREFIX_SEARCH_PATH = getSearchAdminConceptsByPrefixUrl('').replace(
  /\/$/,
  ''
);

/** Refetches the cached unified admin schema view for one concept. */
export async function revalidateAdminSchemaView(cref: string): Promise<void> {
  const key = adminSchemaViewUrl(cref);
  if (key) {
    await mutate(key);
  }
}

// Load the unified admin schema view from the backend.
export function useGetAdminSchemaView(cref: string): [LoadStatus, AdminSchemaViewResponse | null] {
  const url = adminSchemaViewUrl(cref);
  return useGetData<AdminSchemaViewResponse>(url, cref !== '', () =>
    generatedGetAdminSchemaView(cref)
  );
}
//////////////////////////////////////////////////////////////////////
//
// Shared functions for calling the backend.
//

export async function updateFollows(
  req_fields: Partial<UpdateFollowsRequest>
): Promise<DisplayUser> {
  const request: UpdateFollowsRequest = {
    action: UPDATE_FOLLOW_ACTION.Follow,
    ...req_fields,
  };

  return generatedUpdateFollows(request);
}

/** Fetches a marketplace offer by URL and adds it to the default collection. */
export async function addOfferByUrl(url: string): Promise<SuccessResponse> {
  const request: AddOfferByUrlRequest = { url };
  try {
    return await generatedAddOfferToCollectionByUrl(request, { parseErrorBody: false });
  } catch (error: unknown) {
    if (isHttpTransportError(error)) throw new Error('Failed to add product');
    throw error;
  }
}

/** Preserves account-deletion error policy while using its generated request and response. */
export async function deleteAccount(): Promise<ApiContract.DeleteAccountResponse> {
  try {
    return await generatedDeleteAccount({});
  } catch (error: unknown) {
    if (isHttpTransportError(error)) {
      throw new Error('Failed to delete account: Server returned an error');
    }
    if (error instanceof SyntaxError) {
      throw new Error('Invalid response from server: Not valid JSON');
    }
    throw error;
  }
}

/** Updates document and offer membership in one named collection. */
export async function updateCollections(
  request: ApiContract.UpdateCollectionRequest
): Promise<DisplayUser> {
  return generatedUpdateCollections(request);
}

/** Atomically commits the admin schema editor's queued judgments. */
export async function commitAdminSchemaJudgments(
  request: AdminSchemaCommitJudgmentsRequest
): Promise<AdminSchemaCommitJudgmentsResponse> {
  // TODO(1): This non-idempotent POST has no timeout or abort. A bare
  // AbortSignal.timeout() is unsafe because the server may commit after the client loses
  // the response, and retrying the preserved draft would double-submit. Add an idempotency
  // key or a reconciliation protocol before making an ambiguous result retryable.
  try {
    return await generatedCommitAdminSchemaJudgments(request, { parseErrorBody: false });
  } catch (error: unknown) {
    if (!isHttpTransportError(error)) throw error;
    const detail = error instanceof Error ? error.message : 'Request failed';
    throw new Error(`Failed to commit schema judgments: ${detail}`);
  }
}

/** Preserves the UI-oriented signature while delegating transport to Orval. */
export async function castVote(
  docId: PublicDocId,
  voteAction: VoteAction
): Promise<CastVoteResponse> {
  const request: CastVoteRequest = { doc_id: docId, vote_action: voteAction };
  try {
    return await generatedCastVote(request, { parseErrorBody: false });
  } catch (error: unknown) {
    if (!isHttpTransportError(error)) throw error;
    throw prefixedTransportError('Error casting vote', error);
  }
}

/** Sanitized non-success response from the irreversible document-hide endpoint. */
export class HideDocumentError extends Error {
  constructor(public readonly status: number) {
    super(`Unable to hide document (HTTP ${status}).`);
    this.name = 'HideDocumentError';
  }
}

/** Irreversibly hides an authored document and accepts only the empty 204 contract. */
export async function hideDocument(docId: PublicDocId): Promise<void> {
  try {
    await generatedHideDoc(docId, { parseErrorBody: false });
  } catch (error: unknown) {
    if (isHttpTransportError(error)) throw new HideDocumentError(Number(error.status));
    throw error;
  }
}

// Update the user's profile discoverability setting
export async function setProfileDiscoverability(
  discoverability: ProfileDiscoverability
): Promise<DisplayUser> {
  return generatedUpdateDiscoverability({ discoverability });
}

// Update the user's marketplace preference setting
export async function setMarketplacePreference(
  marketplace_preference: MarketplacePreference
): Promise<DisplayUser> {
  return generatedUpdateMarketplacePreference({ marketplace_preference });
}

// Load the current avatar settings and available avatar source options.
export async function getAvatarSettings(): Promise<AvatarSettingsResponse> {
  return generatedGetAvatarSettings();
}

// Update the selected avatar source and its editable configuration.
export async function patchAvatarSettings(
  request: PatchAvatarSettingsRequest
): Promise<AvatarSettingsResponse | AvatarValidationErrorResponse> {
  return generatedPatchAvatarSettings(request);
}

// Request an avatar preview, returning either rendered SVG or validation errors.
export async function postAvatarPreview(
  request: AvatarPreviewRequest
): Promise<AvatarPreviewResult> {
  const response = await generatedPreviewAvatar(request);
  if (response instanceof Blob) {
    return {
      success: true,
      svg: response,
    };
  }
  return response;
}

// Response from password strength check
type PasswordStrengthResponse = ApiContract.PasswordCheckResponse;

/** Facade names for generated unsupported-email reasons. */
export const EmailUnsupportedReason = {
  Empty: GeneratedEmailUnsupportedReason.empty,
  MultipleAddresses: GeneratedEmailUnsupportedReason.multiple_addresses,
  DisplayNameOrAngleAddress: GeneratedEmailUnsupportedReason.display_name_or_angle_address,
  CommentsOrCfws: GeneratedEmailUnsupportedReason.comments_or_cfws,
  QuotedLocalPart: GeneratedEmailUnsupportedReason.quoted_local_part,
  NonAscii: GeneratedEmailUnsupportedReason.non_ascii,
  InternalWhitespace: GeneratedEmailUnsupportedReason.internal_whitespace,
  MissingAtSign: GeneratedEmailUnsupportedReason.missing_at_sign,
  MultipleAtSigns: GeneratedEmailUnsupportedReason.multiple_at_signs,
  InvalidLocalPart: GeneratedEmailUnsupportedReason.invalid_local_part,
  InvalidDomain: GeneratedEmailUnsupportedReason.invalid_domain,
  DomainLiteral: GeneratedEmailUnsupportedReason.domain_literal,
} as const;
export type EmailUnsupportedReason = ApiContract.EmailUnsupportedReasonDto;

/**
 * @public
 * Generated internally status-tagged registration response.
 */
export type RegistrationResponse = GeneratedRegistrationResponse;

/** Facade names for generated email-verification statuses. */
export const VerifyEmailStatus = {
  /** @public */ Success: GeneratedVerifyEmailStatus.success,
  /** @public */ InvalidToken: GeneratedVerifyEmailStatus.invalid_token,
  /** @public */ ExpiredToken: GeneratedVerifyEmailStatus.expired_token,
  /** @public */ EmailAlreadyInUse: GeneratedVerifyEmailStatus.email_already_in_use,
} as const;
export type VerifyEmailStatus = ApiContract.VerifyEmailStatus;

/** Generated request for completing email verification by token. */
type VerifyEmailRequest = ApiContract.VerifyEmailRequest;

/** Generated email-verification response. */
export type VerifyEmailResponse = ApiContract.VerifyEmailResponse;

/** Facade names for generated email-change statuses. */
export const UpdateEmailStatus = {
  /** @public */ PendingVerificationStarted:
    GeneratedUpdateEmailStatus.pending_verification_started,
  /** @public */ UnsupportedFormat: GeneratedUpdateEmailStatus.unsupported_format,
  /** @public */ AlreadyVerifiedByYou: GeneratedUpdateEmailStatus.already_verified_by_you,
  /** @public */ AlreadyPendingForYou: GeneratedUpdateEmailStatus.already_pending_for_you,
  /** @public */ ClaimedByAnotherUser: GeneratedUpdateEmailStatus.claimed_by_another_user,
  /** @public */ EmailRequired: GeneratedUpdateEmailStatus.email_required,
} as const;
export type UpdateEmailStatus = ApiContract.UpdateEmailStatus;

/** Generated response for starting pending email verification. */
export type UpdateEmailResponse = GeneratedUpdateEmailResponse;

/** Facade names for generated resend-verification statuses. */
export const ResendEmailVerificationStatus = {
  /** @public */ VerificationEmailSent:
    GeneratedResendEmailVerificationStatus.verification_email_sent,
  /** @public */ NoPendingVerification:
    GeneratedResendEmailVerificationStatus.no_pending_verification,
} as const;
export type ResendEmailVerificationStatus = ApiContract.ResendEmailVerificationStatus;

/** Generated response for resending pending email verification. */
export type ResendEmailVerificationResponse = ApiContract.ResendEmailVerificationResponse;

/** Facade names for generated stop-verification statuses. */
export const StopEmailVerificationStatus = {
  /** @public */ PendingVerificationStopped:
    GeneratedStopEmailVerificationStatus.pending_verification_stopped,
  /** @public */ NoPendingVerification:
    GeneratedStopEmailVerificationStatus.no_pending_verification,
} as const;
export type StopEmailVerificationStatus = ApiContract.StopEmailVerificationStatus;

/** Generated response for stopping pending email verification. */
export type StopEmailVerificationResponse = ApiContract.StopEmailVerificationResponse;

/**
 * Generated request body for setting or changing the current user's password.
 * `old_password` is omitted for first-time password setup (the backend defaults it).
 */
export type UpdatePasswordRequest = ApiContract.UpdatePasswordRequest;

/**
 * Generated request body for removing a linked OAuth identity from the account.
 */
export type RemoveOAuthIdentityRequest = ApiContract.RemoveOAuthIdentityRequest;

// Password-reset facades over generated contract types and values.

/** Facade names for generated password-reset request statuses. */
export const RequestPasswordResetStatus = {
  /** @public */ Accepted: GeneratedRequestPasswordResetStatus.accepted,
  /** @public */ UnsupportedFormat: GeneratedRequestPasswordResetStatus.unsupported_format,
} as const;
/** Facade names for generated password-reset completion statuses. */
export const ResetPasswordStatus = {
  /** @public */ Success: GeneratedResetPasswordStatus.success,
  /** @public */ InvalidToken: GeneratedResetPasswordStatus.invalid_token,
  /** @public */ ExpiredToken: GeneratedResetPasswordStatus.expired_token,
  /** @public */ PasswordInsecure: GeneratedResetPasswordStatus.password_insecure,
} as const;
export type ResetPasswordStatus = ApiContract.ResetPasswordStatus;

/** Facade names for generated reset-token validation statuses. */
export const ValidateResetTokenStatus = {
  /** @public */ Valid: GeneratedValidateResetTokenStatus.valid,
  /** @public */ InvalidToken: GeneratedValidateResetTokenStatus.invalid_token,
  /** @public */ ExpiredToken: GeneratedValidateResetTokenStatus.expired_token,
} as const;
export type ValidateResetTokenStatus = ApiContract.ValidateResetTokenStatus;

/** Generated reset-token validation request. */
type ValidateResetTokenRequest = ApiContract.ValidateResetTokenRequest;

/** Generated reset-token validation response. */
type ValidateResetTokenResponse = ApiContract.ValidateResetTokenResponse;

/**
 * Facade names for generated offer-post types.
 */
export const OfferPostType = {
  Review: GeneratedOfferPostType.review,
  Question: GeneratedOfferPostType.question,
} as const;
export type OfferPostType = ApiContract.OfferPostType;

/**
 * Generated request for creating a document.
 */
export type NewDocRequest = GeneratedNewDocRequest;

/**
 * Generated successful response from the new-document endpoint.
 */
type NewDocResponse = ApiContract.NewDocResponse;

/** Reports an unsuccessful HTTP response from POST /doc/new. */
export class NewDocRequestError extends Error {
  constructor(public readonly status: number) {
    super(`Failed to post content (${status})`);
    this.name = 'NewDocRequestError';
  }
}

/** Validates and narrows an untrusted successful POST /doc/new response. */
function parseNewDocResponse(value: unknown): NewDocResponse {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('doc_id' in value) ||
    typeof value.doc_id !== 'string' ||
    value.doc_id.trim().length === 0
  ) {
    throw new Error('Invalid new-document response');
  }

  return { doc_id: asPublicDocId(value.doc_id) };
}

/** Creates a document through the typed endpoint contract and validates its response. */
export async function createNewDoc(request: NewDocRequest): Promise<NewDocResponse> {
  let response: NewDocResponse;
  try {
    response = await generatedCreateDoc(request, { parseErrorBody: false });
  } catch (error: unknown) {
    if (typeof error === 'object' && error !== null && 'status' in error) {
      throw new NewDocRequestError(Number(error.status));
    }
    throw error;
  }
  return parseNewDocResponse(response);
}

// Dynamically check password strength.
export async function checkPasswordStrength(password: string): Promise<PasswordStrengthResponse> {
  if (!password) {
    return { strength: 0, is_acceptable: false };
  }

  return generatedCheckPasswordStrength({ password });
}

// Validate a password reset token (read-only operation)
export async function validateResetToken(token: string): Promise<ValidateResetTokenResponse> {
  return generatedValidateResetToken({ token } satisfies ValidateResetTokenRequest);
}

/** Posts a verification token and returns the backend's terminal verification result. */
export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  return generatedVerifyEmail({ token } satisfies VerifyEmailRequest);
}

// Check if a username is available
export async function checkUsernameAvailability(
  username: string
): Promise<{ available: boolean; error?: string }> {
  try {
    const data = await generatedCheckUsernameAvailability({ username });
    return { available: data.available };
  } catch (error: unknown) {
    console.error('Error checking username availability:', error);
    const status =
      typeof error === 'object' && error !== null && 'status' in error ? Number(error.status) : 0;
    const envelope =
      typeof error === 'object' && error !== null && 'data' in error
        ? parseApiErrorEnvelope(error.data)
        : null;
    return {
      available: false,
      error: apiErrorMessage({ operation: 'username_check' }, status, envelope),
    };
  }
}

/** Preserves the established facade wording while retaining generated transport handling. */
function prefixedTransportError(prefix: string, error: unknown): Error {
  const detail =
    error instanceof Error ? error.message.replace(/^HTTP \d+:?\s*/, '') : String(error);
  return new Error(`${prefix}: ${detail}`);
}

/** Distinguishes a received HTTP failure from network and successful-body parsing failures. */
function isHttpTransportError(error: unknown): error is Error & { status: unknown } {
  return typeof error === 'object' && error !== null && 'status' in error;
}

/** Starts verification for a new email while leaving the existing verified email in place. */
export async function startEmailVerification(newEmail: string): Promise<UpdateEmailResponse> {
  try {
    return await generatedUpdateEmail({ new_email: newEmail });
  } catch (error: unknown) {
    if (!isHttpTransportError(error)) throw error;
    throw prefixedTransportError('Error updating email', error);
  }
}

/** Requests a fresh verification email for the current pending email address. */
export async function resendEmailVerification(): Promise<ResendEmailVerificationResponse> {
  try {
    return await generatedResendEmailVerification();
  } catch (error: unknown) {
    if (!isHttpTransportError(error)) throw error;
    throw prefixedTransportError('Error resending verification email', error);
  }
}

/** Abandons the current pending email verification for the signed-in user. */
export async function stopEmailVerification(): Promise<StopEmailVerificationResponse> {
  try {
    return await generatedStopEmailVerification();
  } catch (error: unknown) {
    if (!isHttpTransportError(error)) throw error;
    throw prefixedTransportError('Error stopping email verification', error);
  }
}

/** Payload used for both password-based and OAuth-based registration requests. */
export type RegisterUserRequest = Omit<
  ApiContract.UserToCreate,
  'email' | 'email2' | 'fullname' | 'oauth_identity'
> & {
  fullname: string;
  email: string;
  email2: string;
  oauth_identity?: OAuthIdentityInfo;
};

/** Registers a new user and returns the backend's status-discriminated outcome. */
export async function registerUser(
  registrationData: RegisterUserRequest
): Promise<RegistrationResponse> {
  try {
    return await generatedRegister(registrationData);
  } catch (error: unknown) {
    const status =
      typeof error === 'object' && error !== null && 'status' in error ? Number(error.status) : 0;
    const envelope =
      typeof error === 'object' && error !== null && 'data' in error
        ? parseApiErrorEnvelope(error.data)
        : null;
    if (status > 0) {
      throw new Error(apiErrorMessage({ operation: 'registration' }, status, envelope));
    }
    if (error instanceof SyntaxError) {
      throw new Error('The server returned an invalid response');
    }
    throw new Error('A network error occurred');
  }
}

// =============================================================================
// Integration Health Dashboard generated aliases and stable application names
// =============================================================================

/** Stable application names for generated integration-health states. */
export const HealthStatus = {
  Healthy: GeneratedHealthStatus.healthy,
  Degraded: GeneratedHealthStatus.degraded,
  Unhealthy: GeneratedHealthStatus.unhealthy,
} as const;
export type HealthStatus = ApiContract.HealthStatus;

/** Stable application names for generated integration-health events. */
export const HealthEvent = {
  Ok: GeneratedHealthEvent.ok,
  Timeout: GeneratedHealthEvent.timeout,
  Network: GeneratedHealthEvent.network,
  ParseFail: GeneratedHealthEvent.parse_fail,
  RateLimited: GeneratedHealthEvent.rate_limited,
  Auth: GeneratedHealthEvent.auth,
  Server5xx: GeneratedHealthEvent.server5xx,
  Client4xx: GeneratedHealthEvent.client4xx,
  Other: GeneratedHealthEvent.other,
} as const;
export type HealthEvent = ApiContract.HealthEvent;

/** Generated timestamped health event. */
type TimestampedHealthEvent = ApiContract.TimestampedHealthEvent;

/** Generated health report for one client. */
export type ClientHealthReport = ApiContract.DisplayClientHealthReport;

/** Generated routing-table entry. */
export type RoutingEntry = ApiContract.DisplayRoutingEntry;

/** Generated catalog routing summary. */
export type CatalogRouting = ApiContract.DisplayCatalogRouting;

/** Generated marketplace routing summary. */
export type MarketplaceRouting = ApiContract.DisplayMarketplaceRouting;

/** Generated orchestrator health projection. */
export type OrchestratorHealth = ApiContract.DisplayOrchestratorHealth;

/** Generated integration-health dashboard response. */
export type IntegrationHealthReport = ApiContract.IntegrationHealthReport;
